import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Building2, Users, TrendingUp, Shield, ArrowRight, Sparkles, Target, 
  Award, Check, Layers, Gem, Zap, Hand, 
  Building, Phone
} from "lucide-react";
import ImageCarousel from "../components/common/ImageCarousel";
import PropertyCard from "../components/common/PropertyCard";
import LeadCaptureModal from "../components/common/LeadCaptureModal";
import DevelopersLogoCarousel from "../components/home/DevelopersLogoCarousel";
import { HERO_SLIDES, LEAD_CHANNELS, CONTACT_PHONE, CONTACT_WHATSAPP } from "../constants/data";
import { projectsAPI, mapProjectToProperty, projectFirstImage } from "../services/api";
import AreaMapExplorer from "../components/home/AreaMapExplorer";

const GOLD = "#50C878";
const NAVY = "#013220";
const WHATSAPP_GREEN = "#25D366";

export default function HomePage({ stateCode = "tg", stateName = "Telangana" }) {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [featuredProps, setFeaturedProps] = useState([]);
  const heroSlides = HERO_SLIDES;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        let res = await projectsAPI.list({ featured: "true", limit: 6 });
        let rows = res.data || [];
        if (!rows.length) {
          res = await projectsAPI.list({ limit: 4 });
          rows = res.data || [];
        }
        const mapped = rows.map(mapProjectToProperty);
        if (alive) setFeaturedProps(mapped);

        const entries = await Promise.all(
          mapped.map(async (p) => [
            p.id,
            await projectFirstImage(p.builderRaw, p.projectRaw),
          ])
        );
        if (!alive) return;
        const projectImg = new Map(entries);
        setFeaturedProps((prev) =>
          prev.map((p) => {
            const url = projectImg.get(p.id);
            return url ? { ...p, images: [url] } : p;
          })
        );
      } catch (_) {
        if (alive) setFeaturedProps([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div style={{ background: "#F2F8F3", color: NAVY, minHeight: "100vh" }}>

      {/* ── HERO CAROUSEL ──────────────────────────────── */}
      <ImageCarousel
        slides={heroSlides}
        autoPlay
        showControls
        showDots
        aspectRatio="hero"
        onCtaClick={() => setShowModal(true)}
        loop={false}
      />

      {/* ── NEW: ABOUT GROUPBUY FLATS SECTION ────────────── */}
      <section className="gbf-about-section" style={{ padding: "70px 24px 50px", background: "#F2F8F3", position: "relative" }}>
        {/* Decorative background element */}
        <div style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "40%",
          height: "100%",
          background: "radial-gradient(ellipse at top right, rgba(80,200,120,0.04) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />
        
        <div style={{ maxWidth: "1100px", margin: "0 auto", position: "relative" }}>
          
          {/* Section Header */}
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <span style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "0.7rem",
              color: GOLD,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              display: "block",
              marginBottom: "12px",
            }}>
              Revolutionizing Real Estate
            </span>
            <h2 className="gbf-heading-xl" style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: 600,
              color: NAVY,
              margin: 0,
              lineHeight: 1.2,
            }}>
              The Future of <span style={{ color: GOLD }}>Collective Buying</span>
            </h2>
            <div style={{
              width: "60px",
              height: "3px",
              background: GOLD,
              margin: "16px auto 0",
              borderRadius: "2px",
            }} />
          </div>

          {/* About Us - Main Card with Accent */}
          <div className="gbf-about-card" style={{
            background: "linear-gradient(145deg, #FFFFFF 0%, #FAFDFB 100%)",
            border: "1px solid rgba(80,200,120,0.12)",
            borderRadius: "20px",
            padding: "48px 50px",
            marginBottom: "32px",
            boxShadow: "0 8px 40px rgba(0,0,0,0.04)",
            position: "relative",
            overflow: "hidden",
          }}>
            {/* Decorative accent line */}
            <div style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "4px",
              height: "100%",
              background: `linear-gradient(to bottom, ${GOLD}, rgba(80,200,120,0.2))`,
            }} />
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "40px", alignItems: "start" }}>
              <div>
                <h3 className="gbf-heading-lg" style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "1.8rem",
                  fontWeight: 600,
                  color: NAVY,
                  margin: "0 0 16px",
                }}>
                  About GroupBuy Flats
                </h3>
                <p className="gbf-body-text" style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "1.05rem",
                  color: "rgba(1,50,32,0.78)",
                  lineHeight: 1.9,
                  margin: "0 0 20px",
                }}>
                  GroupBuy Flats is a next-gen real estate company specializing in bulk property transactions and strategic investment.
                  We act as a bridge between real estate developers and Buyers/Investors by aggregating demand and structuring high-value group deals.
                </p>
                <div className="gbf-pill-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "16px" }}>
                  {[
                    { icon: Gem, label: "Better pricing than market" },
                    { icon: Zap, label: "Faster inventory liquidation" },
                    { icon: Hand, label: "Optimized Investment Opportunity" },
                  ].map(({ icon: Icon, label }) => (
                    <div key={label} style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      background: "rgba(80,200,120,0.06)",
                      padding: "10px 16px",
                      borderRadius: "10px",
                      border: "1px solid rgba(80,200,120,0.06)",
                    }}>
                      <Icon size={16} color={GOLD} />
                      <span style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: "0.9rem",
                        color: "rgba(1,50,32,0.8)",
                        fontWeight: 500,
                      }}>{label}</span>
                    </div>
                  ))}
                </div>
                <p className="gbf-quote-text" style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "1rem",
                  color: "rgba(1,50,32,0.65)",
                  lineHeight: 1.8,
                  margin: 0,
                  fontStyle: "italic",
                }}>
                  "Our model transforms traditional real estate transactions into efficient, transparent, and value-driven collaborations."
                </p>
              </div>
            </div>
          </div>

          {/* Vision & Mission - Side by Side with Icons */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "24px", marginBottom: "32px" }}>
            <div className="gbf-hover-card" style={{
              background: "linear-gradient(145deg, #FFFFFF 0%, #FAFDFB 100%)",
              border: "1px solid rgba(80,200,120,0.12)",
              borderRadius: "16px",
              padding: "32px 30px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseOver={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 8px 30px rgba(80,200,120,0.08)"; }}
            onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.03)"; }}>
              <div style={{
                width: "52px",
                height: "52px",
                borderRadius: "12px",
                background: `linear-gradient(135deg, rgba(80,200,120,0.12), rgba(80,200,120,0.04))`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "16px",
              }}>
                <Target size={24} color={GOLD} />
              </div>
              <h3 className="gbf-heading-lg" style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "1.5rem",
                fontWeight: 600,
                color: NAVY,
                margin: "0 0 12px",
              }}>
                Our Vision
              </h3>
              <p className="gbf-body-text" style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "0.98rem",
                color: "rgba(1,50,32,0.72)",
                lineHeight: 1.8,
                margin: 0,
              }}>
                To become India's most trusted and scalable platform for unified real estate investments by delivering value, transparency, and long-term growth.
              </p>
            </div>

            <div className="gbf-hover-card" style={{
              background: "linear-gradient(145deg, #FFFFFF 0%, #FAFDFB 100%)",
              border: "1px solid rgba(80,200,120,0.12)",
              borderRadius: "16px",
              padding: "32px 30px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseOver={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 8px 30px rgba(80,200,120,0.08)"; }}
            onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.03)"; }}>
              <div style={{
                width: "52px",
                height: "52px",
                borderRadius: "12px",
                background: `linear-gradient(135deg, rgba(80,200,120,0.12), rgba(80,200,120,0.04))`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "16px",
              }}>
                <Award size={24} color={GOLD} />
              </div>
              <h3 className="gbf-heading-lg" style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "1.5rem",
                fontWeight: 600,
                color: NAVY,
                margin: "0 0 12px",
              }}>
                Our Mission
              </h3>
              <div style={{ display: "grid", gap: "10px" }}>
                {[
                  "Empower buyers with Big Savings through collective negotiation",
                  "Empower developers to accelerate inventory absorption",
                  "Create Win-Win Opportunities for all Stakeholders",
                  "Bring structure, transparency, and trust into bulk real estate",
                ].map((item) => (
                  <div key={item} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                    <Check size={16} color={GOLD} style={{ marginTop: "3px", flexShrink: 0 }} />
                    <span className="gbf-body-text" style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.92rem",
                      color: "rgba(1,50,32,0.72)",
                      lineHeight: 1.6,
                    }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* The Concept - Full Width with Gradient Background */}
          <div className="gbf-concept-card" style={{
            background: "linear-gradient(135deg, rgba(80,200,120,0.06) 0%, rgba(80,200,120,0.02) 100%)",
            border: "1px solid rgba(80,200,120,0.15)",
            borderRadius: "16px",
            padding: "36px 44px",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}>
            <div style={{
              position: "absolute",
              top: "-50%",
              right: "-20%",
              width: "300px",
              height: "300px",
              borderRadius: "50%",
              background: "rgba(80,200,120,0.03)",
              pointerEvents: "none",
            }} />
            <div style={{ position: "relative", zIndex: 1 }}>
              <Layers size={28} color={GOLD} style={{ marginBottom: "12px" }} />
              <h3 className="gbf-heading-lg" style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "1.6rem",
                fontWeight: 600,
                color: NAVY,
                margin: "0 0 12px",
              }}>
                The Concept
              </h3>
              <p className="gbf-body-text" style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "1.05rem",
                color: "rgba(1,50,32,0.78)",
                lineHeight: 1.9,
                margin: 0,
                maxWidth: "700px",
                marginLeft: "auto",
                marginRight: "auto",
              }}>
                At the core of our operations is the <strong style={{ color: GOLD }}>Power of Group Buying</strong>, 
                where multiple buyers come together to purchase properties in bulk. This enables 
                <strong style={{ color: GOLD }}> Bulk Deal Negotiation</strong> and 
                <strong style={{ color: GOLD }}> Exclusive Pricing Advantages</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ──────────────────────────────────── */}
      <div
        className="gbf-stats-bar"
        style={{
          background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)",
          borderTop: `2px solid ${GOLD}`,
          borderBottom: "1px solid rgba(80,200,120,0.15)",
          padding: "28px 24px",
        }}
      >
        <div
          className="gbf-stats-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "24px",
            textAlign: "center",
            maxWidth: "800px",
            margin: "0 auto",
          }}
        >
          {[
            { icon: Building2, value: "35+", label: "Properties Listed" },
            { icon: TrendingUp, value: "27+", label: "Builder Partners" },
            { icon: Shield, value: "100%", label: "RERA Approved" },
          ].map(({ icon: Icon, value, label }) => (
            <div key={label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
              <Icon size={22} color={GOLD} />
              <span className="gbf-stat-value" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2rem", fontWeight: 700, color: NAVY, lineHeight: 1 }}>
                {value}
              </span>
              <span className="gbf-stat-label" style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.78rem", color: "rgba(1,50,32,0.62)", letterSpacing: "0.08em" }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── PROUD TO HAVE ASSOCIATION WITH OUR PARTNERS ── */}
      <section className="gbf-section-pad" style={{ padding: "52px 24px", background: "#F7FBF8", borderBottom: "1px solid rgba(80,200,120,0.1)" }}>
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "36px" }}>
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase", display: "block", marginBottom: "10px" }}>
              Our Builder Network
            </span>
            <h2 className="gbf-heading-md" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.5rem, 3vw, 2.2rem)", fontWeight: 600, color: NAVY, margin: 0 }}>
              Proudly Associated with Our Partner Builders
            </h2>
          </div>

          <DevelopersLogoCarousel />

          <p style={{
            textAlign: "center",
            color: "rgba(1,50,32,0.48)",
            fontFamily: "'Inter',sans-serif",
            fontSize: "1.1rem",
            marginTop: "26px"
          }}>
            <span style={{ color: GOLD }}>Tap any logo to explore their projects</span>
          </p>
        </div>
      </section>

      {/* ── HOW IT WORKS ───────────────────────────────── */}
      <section className="gbf-section-pad" style={{ padding: "80px 24px" }}>
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "56px" }}>
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase", display: "block", marginBottom: "12px" }}>
              The GroupBy Advantage
            </span>
            <h2 className="gbf-heading-xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 4vw, 3rem)", fontWeight: 600, color: NAVY, margin: 0 }}>
              Better Together. Better Deals.
            </h2>
          </div>
          <div className="gbf-steps-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "2px" }}>
            {[
              { num: "01", title: "Register Your Interest", desc: "Tell us what you're looking for — area, budget, type. No commitment required." },
              { num: "02", title: "Join a Buying Group", desc: "We match you with other buyers targeting the same builder or project." },
              { num: "03", title: "We Negotiate as One", desc: "Our team approaches builders with collective demand — 50 buyers talk louder than one." },
              { num: "04", title: "You Get the Best Deal", desc: "You receive the best negotiated price on the day, with full transparency." },
            ].map(({ num, title, desc }) => (
              <div
                key={num}
                style={{
                  padding: "36px 28px",
                  background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)",
                  border: "1px solid rgba(80,200,120,0.15)",
                  position: "relative",
                  overflow: "hidden",
                  transition: "border-color 0.2s",
                }}
                onMouseOver={e => e.currentTarget.style.borderColor = "rgba(80,200,120,0.4)"}
                onMouseOut={e => e.currentTarget.style.borderColor = "rgba(80,200,120,0.15)"}
              >
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "4rem", fontWeight: 700, color: "rgba(80,200,120,0.08)", position: "absolute", top: "8px", right: "16px", lineHeight: 1 }}>
                  {num}
                </div>
                <div style={{ width: "40px", height: "2px", background: GOLD, marginBottom: "20px" }} />
                <h3 className="gbf-heading-lg" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem", fontWeight: 600, color: NAVY, marginBottom: "12px" }}>
                  {title}
                </h3>
                <p className="gbf-body-text" style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.88rem", color: "rgba(1,50,32,0.62)", lineHeight: 1.7 }}>
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURED PROPERTIES ─────────────────────────── */}
      <section className="gbf-section-pad" style={{ padding: "0 24px 80px" }}>
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <div className="gbf-featured-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "40px" }}>
            <div>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>
                Handpicked For You
              </span>
              <h2 className="gbf-heading-lg2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 600, color: NAVY, margin: 0 }}>
                Featured Properties
              </h2>
            </div>
            <button
              onClick={() => navigate("/properties")}
              className="gbf-viewall-btn"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "transparent",
                border: `1px solid ${GOLD}`,
                borderRadius: "4px",
                padding: "10px 20px",
                color: GOLD,
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: "0.85rem",
                cursor: "pointer",
                letterSpacing: "0.04em",
                transition: "background 0.2s",
              }}
              onMouseOver={e => e.currentTarget.style.background = "rgba(80,200,120,0.1)"}
              onMouseOut={e => e.currentTarget.style.background = "transparent"}
            >
              View All <ArrowRight size={14} />
            </button>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "24px" }}
            className="properties-grid"
          >
            {featuredProps.map(prop => (
              <PropertyCard key={prop.id} property={prop} />
            ))}
          </div>
        </div>
      </section>

      {/* ── MAP SECTION ──────────────────────────────────── */}
      <section
        className="gbf-section-pad"
        style={{
          padding: "80px 24px",
          background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)",
          borderTop: "1px solid rgba(80,200,120,0.1)",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "40px" }}>
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase", display: "block", marginBottom: "12px" }}>
              Search By Location
            </span>
            <h2 className="gbf-heading-lg2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 600, color: NAVY, marginBottom: "16px" }}>
              Find Properties on the Map
            </h2>
            <p style={{ color: "rgba(1,50,32,0.62)", fontFamily: "'Inter', sans-serif", fontSize: "0.95rem" }}>
              Browse our listings by location across Hyderabad — click a pin to explore.
            </p>
          </div>

          <AreaMapExplorer />
        </div>
      </section>

      {/* ── CTA BANNER ───────────────────────────────────── */}
      <section
        className="gbf-cta-section"
        style={{
          padding: "80px 24px",
          textAlign: "center",
          background: "linear-gradient(135deg, rgba(80,200,120,0.12) 0%, rgba(127,217,160,0.06) 100%)",
          borderTop: "1px solid rgba(80,200,120,0.2)",
          borderBottom: "1px solid rgba(80,200,120,0.2)",
        }}
      >
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase" }}>
          Start Today
        </span>
        <h2 className="gbf-heading-xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3.2rem)", fontWeight: 600, color: NAVY, margin: "12px 0 16px" }}>
          Ready to Buy Smarter?
        </h2>
        <p className="gbf-body-text" style={{ color: "rgba(1,50,32,0.85)", fontFamily: "'Inter', sans-serif", fontSize: "1rem", maxWidth: "500px", margin: "0 auto 32px", lineHeight: 1.7 }}>
          Register your interest today and our associates will walk you through everything — no commitment, no pressure.
        </p>
        <button
          onClick={() => setShowModal(true)}
          className="gbf-cta-btn"
          style={{
            background: "linear-gradient(135deg, #50C878, #7FD9A0)",
            color: "#FFFFFF",
            border: "none",
            padding: "16px 48px",
            borderRadius: "4px",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            fontSize: "1rem",
            cursor: "pointer",
            letterSpacing: "0.05em",
            transition: "transform 0.2s, box-shadow 0.2s",
          }}
          onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(80,200,120,0.4)"; }}
          onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
        >
          Talk To An Expertise
        </button>
      </section>

      {/* Lead Modal */}
      <LeadCaptureModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        channel={LEAD_CHANNELS.WEB}
      />

      {/* ── MOBILE QUICK ACTIONS: WhatsApp & Call ──────────
          Fixed bottom-right, sits directly ABOVE the chatbot
          launcher. Hidden on desktop/tablet — mobile only. ── */}
      <div className="gbf-mobile-quick-actions">
        <a
          href={CONTACT_WHATSAPP}
          target="_blank"
          rel="noopener noreferrer"
          className="gbf-quick-action-btn gbf-whatsapp-btn"
          aria-label="Chat with us on WhatsApp"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="#FFFFFF" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 18.15h-.01c-1.5 0-2.97-.4-4.24-1.17l-.3-.18-3.12.82.83-3.04-.2-.31a8.15 8.15 0 0 1-1.25-4.36c0-4.51 3.67-8.18 8.19-8.18a8.14 8.14 0 0 1 8.18 8.18c0 4.51-3.67 8.24-8.08 8.24zm4.48-6.13c-.24-.12-1.44-.71-1.66-.79-.22-.08-.38-.12-.54.12-.16.24-.62.79-.76.95-.14.16-.28.18-.52.06-1.42-.71-2.35-1.27-3.28-2.87-.25-.42.25-.39.71-1.31.08-.16.04-.3-.04-.42-.08-.12-.54-1.3-.74-1.78-.2-.48-.4-.42-.55-.42-.14 0-.3-.02-.46-.02-.16 0-.42.06-.64.3-.22.24-.85.83-.85 2.02 0 1.19.87 2.34 1 2.5.12.16 1.64 2.5 3.98 3.4 1.98.76 2.38.61 2.82.57.44-.04 1.44-.59 1.64-1.16.2-.57.2-1.06.14-1.16-.06-.1-.24-.16-.48-.28z"/>
          </svg>
        </a>
        <a
          href={`tel:+91${CONTACT_PHONE.replace(/\s/g, "")}`}
          className="gbf-quick-action-btn gbf-call-btn"
          aria-label="Call us"
        >
          <Phone size={26} color="#FFFFFF" strokeWidth={2.2} />
        </a>
      </div>

      <style>{`
        /* ============ TABLET (≤ 1024px) ============ */
        @media (max-width: 1024px) {
          .gbf-about-card > div {
            grid-template-columns: 1fr !important;
          }
        }

        /* ============ MOBILE QUICK ACTIONS (WhatsApp / Call) ============ */
        .gbf-mobile-quick-actions {
          display: none; /* desktop/tablet: hidden, chatbot alone is enough */
        }

        @media (max-width: 640px) {
          .gbf-mobile-quick-actions {
            display: flex;
            flex-direction: column;
            gap: 14px;
            position: fixed;
            right: 20px;
            bottom: 88px;    /* tightened so the gap above the chatbot matches the gap between our own two buttons */
            z-index: 999;    /* just under the chatbot's own z-index */
          }
          .gbf-quick-action-btn {
            width: 56px;     /* matched to the chatbot launcher's size */
            height: 56px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            box-shadow: 0 4px 16px rgba(0,0,0,0.22);
            transition: transform 0.2s, box-shadow 0.2s;
          }
          .gbf-quick-action-btn:active {
            transform: scale(0.94);
          }
          .gbf-whatsapp-btn {
            background: #25D366;
          }
          .gbf-call-btn {
            background: linear-gradient(135deg, #50C878, #7FD9A0);
          }
        }

        /* ============ MOBILE (≤ 640px) ============ */
        @media (max-width: 640px) {
          /* Properties grid */
          .properties-grid { grid-template-columns: 1fr !important; }

          /* About section */
          .gbf-about-section { padding: 44px 16px 32px !important; }
          .gbf-about-card { padding: 28px 20px !important; border-radius: 14px !important; }
          .gbf-about-card > div { gap: 0 !important; }
          .gbf-pill-grid { grid-template-columns: 1fr !important; }
          .gbf-concept-card { padding: 28px 20px !important; }

          /* Headings scale down */
          .gbf-heading-xl { font-size: 1.7rem !important; }
          .gbf-heading-lg { font-size: 1.25rem !important; }
          .gbf-heading-lg2 { font-size: 1.55rem !important; }
          .gbf-heading-md { font-size: 1.3rem !important; }

          /* Body copy */
          .gbf-body-text { font-size: 0.92rem !important; line-height: 1.7 !important; }
          .gbf-quote-text { font-size: 0.9rem !important; }

          /* Stats bar */
          .gbf-stats-bar { padding: 20px 16px !important; }
          .gbf-stats-grid { gap: 10px !important; }
          .gbf-stat-value { font-size: 1.4rem !important; }
          .gbf-stat-label { font-size: 0.62rem !important; letter-spacing: 0.02em !important; }

          /* Generic section padding */
          .gbf-section-pad { padding-left: 16px !important; padding-right: 16px !important; }

          /* How it works steps */
          .gbf-steps-grid { grid-template-columns: 1fr !important; }

          /* Featured properties header */
          .gbf-featured-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 16px !important;
          }
          .gbf-viewall-btn {
            width: 100% !important;
            justify-content: center !important;
          }

          /* CTA section */
          .gbf-cta-section { padding: 56px 20px !important; }
          .gbf-cta-btn { width: 100% !important; padding: 16px 24px !important; }
        }

        /* ============ SMALL MOBILE (≤ 400px) ============ */
        @media (max-width: 400px) {
          .gbf-heading-xl { font-size: 1.45rem !important; }
          .gbf-heading-lg2 { font-size: 1.35rem !important; }
        }
      `}</style>
    </div>
  );
}